/**
 * Service for fetching and caching business unit data
 */

import truckService from '../pages/tiles/truck_scheduler/components/services/truckService';

// Cache business units to avoid repeated API calls
let businessUnitsCache = {
  data: [],
  lastFetched: null,
  expiresIn: 15 * 60 * 1000 // 15 minutes cache expiration
};

const businessUnitService = {
  /**
   * Fetch all business units from the API or return cached data if available
   * @param {boolean} forceRefresh - Force a refresh of the cache
   * @returns {Promise<Array>} - List of business units
   */
  getAllBusinessUnits: async (forceRefresh = false) => {
    // Return cached data if available and not expired
    const now = new Date();
    if (
      !forceRefresh &&
      businessUnitsCache.data.length > 0 && 
      businessUnitsCache.lastFetched && 
      (now.getTime() - businessUnitsCache.lastFetched.getTime()) < businessUnitsCache.expiresIn
    ) {
      return businessUnitsCache.data;
    }

    // Fetch fresh data from API
    try {
      const response = await truckService.getAll('business-units');

      // Format the response to ensure consistent structure
      let formattedUnits = [];
      
      if (Array.isArray(response)) {
        // Map the response to ensure consistent ID/name format
        formattedUnits = response.map(unit => ({
          id: unit.id || unit.business_unit_id || unit.value || unit,
          name: unit.name || unit.business_unit_name || unit.label || unit
        }));
      } else if (response && response.data && Array.isArray(response.data)) {
        // Handle nested data structure if present
        formattedUnits = response.data.map(unit => ({
          id: unit.id || unit.business_unit_id || unit.value || unit,
          name: unit.name || unit.business_unit_name || unit.label || unit
        }));
      }

      // Update cache
      businessUnitsCache = {
        data: formattedUnits,
        lastFetched: now,
        expiresIn: businessUnitsCache.expiresIn
      };

      return formattedUnits;
    } catch (error) {
      console.error('Error fetching business units:', error);
      
      // If there's cached data, return it despite being expired
      if (businessUnitsCache.data.length > 0) {
        return businessUnitsCache.data;
      }
      
      // Fallback to default data if API call fails and no cache is available
      const fallbackUnits = [
        { id: '1', name: 'Electronics' },
        { id: '2', name: 'Furniture' },
        { id: '3', name: 'Apparel' },
        { id: '4', name: 'Automotive' }
      ];
      
      // Update cache with fallback data
      businessUnitsCache = {
        data: fallbackUnits,
        lastFetched: now,
        expiresIn: 5 * 60 * 1000 // Shorter expiration for fallback data (5 minutes)
      };
      
      return fallbackUnits;
    }
  },

  /**
   * Get business unit name by ID
   * @param {string|number} id - Business unit ID
   * @returns {Promise<string>} - Business unit name or the ID if not found
   */
  getBusinessUnitName: async (id) => {
    if (!id) return '';
    
    // Convert id to string for comparison
    const businessUnitId = String(id);
    
    // Get all business units
    const businessUnits = await businessUnitService.getAllBusinessUnits();
    
    // Find matching business unit
    const businessUnit = businessUnits.find(unit => String(unit.id) === businessUnitId);
    
    // Return name or ID as fallback
    return businessUnit ? businessUnit.name : businessUnitId;
  },

  /**
   * Clear the business units cache
   */
  clearCache: () => {
    businessUnitsCache = {
      data: [],
      lastFetched: null,
      expiresIn: businessUnitsCache.expiresIn
    };
  }
};

export default businessUnitService;